/**
 * Attachments Service
 * API operations for file attachments with upload progress
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type { AttachmentResponseDto } from '@/types/api/responses';

/**
 * Callback type for tracking upload progress.
 * @param percent - Upload progress percentage (0-100)
 */
export type UploadProgressCallback = (percent: number) => void;

export const attachmentsService = {
  /**
   * Upload a file attachment to a task.
   * Uses multipart/form-data for file upload with progress tracking.
   *
   * @param taskId - Task ID to attach file to
   * @param file - File to upload
   * @param onProgress - Optional progress callback receiving percentage (0-100)
   * @returns Promise with the created attachment metadata
   */
  upload: (taskId: string, file: File, onProgress?: UploadProgressCallback) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post<AttachmentResponseDto>(
      API_ENDPOINTS.ATTACHMENTS.BASE(taskId),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percent);
          }
        },
      }
    );
  },

  /**
   * Same-origin URL of the download endpoint. Use with `<a href download>` so
   * the browser follows the 302 to the storage signed URL natively (no CORS).
   */
  getDownloadUrl: (attachmentId: string) =>
    API_ENDPOINTS.ATTACHMENTS.DOWNLOAD(attachmentId),

  /**
   * Same-origin streaming preview — needed by in-browser viewers that consume
   * raw bytes (PDF / DOCX / image). Bypasses the cross-origin redirect so axios
   * arraybuffer works without bucket CORS.
   */
  fetchPreview: (attachmentId: string) =>
    apiClient.get<ArrayBuffer>(
      API_ENDPOINTS.ATTACHMENTS.PREVIEW(attachmentId),
      {
        responseType: 'arraybuffer',
      }
    ),

  /**
   * Delete an attachment from a task.
   *
   * @param taskId - Task ID the attachment belongs to
   * @param attachmentId - Attachment ID to delete
   * @returns Promise that resolves on successful deletion
   */
  delete: (taskId: string, attachmentId: string) =>
    apiClient.delete(API_ENDPOINTS.ATTACHMENTS.DELETE(taskId, attachmentId)),
};
